/* eslint-disable camelcase */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import Select from 'react-select';

import ProgressBar from '../../ui/ProgressBar';
import Button from '../../ui/Button';
import { focusGlowStyle } from '../../ui/sharedStyles';
import {
  fetchDeepSEAFeatures,
  fetchInsilicoData,
  setSelectedDeepSEAJob,
  setSelectedInsilicoData,
} from '../../../actions/DeepSEAActions';
import { saveText } from '../../../core/save';
import createTabDelimitedInsilico from './DownloadInsilico';

// TypeScript interfaces
interface Feature {
  cell_type: {
    name: string;
  };
  chromatin_feature_type: {
    name: string;
  };
  treatment: {
    name: string;
  };
}

interface InsilicoPosition {
  // TODO: Define the structure based on actual data
  // This is a placeholder and should be updated
  [key: string]: any;
}

interface InsilicoData {
  [featureName: string]: {
    positions: InsilicoPosition[];
  };
}

interface DeepSeaState {
  selectedJob: {
    ds_model?: string;
    sequence_names?: string[];
  };
  jobs: {
    [jobId: string]: any;
  };
  features: Feature[];
  in_silico_mutagenesis: {
    [sequenceName: string]: InsilicoData;
  };
}

interface RootState {
  deepsea: DeepSeaState;
}

interface SelectOption {
  value: string;
  label: string;
}

interface InsilicoFormProps {
  jobId: string;
}

function _getSequenceOption(seq: string): SelectOption {
  return { value: seq, label: seq };
}

function _getFeatureOption(feature: Feature, idx: number): SelectOption {
  const { cell_type: cell, chromatin_feature_type: chrom, treatment } = feature;
  return {
    value: `${idx}|${cell.name}|${chrom.name}|${treatment.name}`,
    label: `Index: ${idx} |
    Cell type: ${cell.name} |
    Chromatin feature type: ${chrom.name} |
    Treatment: ${treatment.name}`,
  };
}

export const InsilicoForm: React.FC<InsilicoFormProps> = ({ jobId }) => {
  const dispatch = useDispatch();
  const {
    ds_model: dsModel = 'Beluga', // default model
    sequence_names: sequenceNames,
  } = useSelector((state: RootState) => state.deepsea.selectedJob);
  const jobs = useSelector((state: RootState) => state.deepsea.jobs);
  const deepseaFeatures = useSelector(
    (state: RootState) => state.deepsea.features,
  );
  const insilico = useSelector(
    (state: RootState) => state.deepsea.in_silico_mutagenesis,
  );

  const progress = (
    <ProgressBar
      label="Retrieving job sequences and features..."
      style={{
        width: '80%',
        marginLeft: 'auto',
        marginRight: 'auto',
      }}
    />
  );

  useEffect(() => {
    dispatch(setSelectedDeepSEAJob(jobs[jobId]));
  }, [jobId]);

  useEffect(() => {
    dispatch(fetchDeepSEAFeatures(dsModel));
  }, [dsModel]);

  let featureOptions: SelectOption[] = [];
  if (deepseaFeatures && deepseaFeatures.length) {
    featureOptions = deepseaFeatures.map((feature, idx) =>
      _getFeatureOption(feature, idx),
    );
  }

  let sequenceOptions: SelectOption[] = [];
  if (sequenceNames && sequenceNames.length) {
    sequenceOptions = sequenceNames.map(sequence =>
      _getSequenceOption(sequence),
    );
  }

  interface FormValues {
    sequence_name: string;
    feature_name: string;
  }

  return (
    <div>
      {deepseaFeatures &&
      deepseaFeatures.length &&
      sequenceNames &&
      sequenceNames.length ? (
        <Formik<FormValues>
          enableReinitialize
          initialStatus={{
            featureOnly: false,
          }}
          initialValues={{
            sequence_name:
              sequenceNames && sequenceNames.length
                ? _getSequenceOption(sequenceNames[0]).value
                : '',
            feature_name:
              deepseaFeatures && deepseaFeatures.length
                ? _getFeatureOption(deepseaFeatures[0], 0).value
                : '',
          }}
          onSubmit={values => {
            if (
              values.sequence_name in insilico &&
              values.feature_name in insilico[values.sequence_name]
            ) {
              dispatch(setSelectedInsilicoData(values));
            } else dispatch(fetchInsilicoData(jobId, values));
          }}
        >
          {({ values, handleSubmit, setFieldValue }) => (
            <form encType="multipart/form-data" onSubmit={handleSubmit}>
              <div style={{ marginBottom: '15px' }}>
                <label
                  htmlFor="formInsilicoSequence"
                  style={{
                    display: 'block',
                    marginBottom: '5px',
                    fontWeight: 'bold',
                  }}
                >
                  {`Sequences (${sequenceOptions.length}):`}
                </label>
                <Select
                  defaultValue={sequenceOptions[0]}
                  isSearchable={false}
                  onChange={e => setFieldValue('sequence_name', e?.value)}
                  options={sequenceOptions}
                  value={sequenceOptions.find(
                    option => option.value === values.sequence_name,
                  )}
                  id="formInsilicoSequence"
                  styles={focusGlowStyle}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label
                  htmlFor="formInsilicoFeature"
                  style={{
                    display: 'block',
                    marginBottom: '5px',
                    fontWeight: 'bold',
                  }}
                >
                  {`Chromatin features (${featureOptions.length}):`}
                </label>
                <Select
                  defaultValue={featureOptions[0]}
                  onChange={e => setFieldValue('feature_name', e?.value)}
                  options={featureOptions}
                  value={featureOptions.find(
                    option => option.value === values.feature_name,
                  )}
                  id="formInsilicoFeature"
                  styles={focusGlowStyle}
                />
              </div>
              <Button bsStyle="primary" type="submit">
                Submit
              </Button>
              {insilico[values.sequence_name] &&
              insilico[values.sequence_name][values.feature_name] ? (
                <Button
                  bsStyle="default"
                  style={{ marginLeft: 10 }}
                  onClick={() => {
                    const ftrName = values.feature_name;
                    const seqName = values.sequence_name;
                    const fileName = `${seqName}_${ftrName}_${Date.now()}.csv`;
                    const textContent = createTabDelimitedInsilico(
                      ftrName,
                      seqName,
                      insilico[seqName][ftrName].positions,
                    );
                    saveText(textContent, fileName);
                  }}
                >
                  <span role="button" tabIndex={-1}>
                    Download CSV
                  </span>
                </Button>
              ) : (
                ''
              )}
            </form>
          )}
        </Formik>
      ) : (
        progress
      )}
    </div>
  );
};

export default InsilicoForm;
